import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  CircularProgress,
  LinearProgress,
  Badge,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Score as ScoreIcon,
  TrendingUp,
  SportsTennis,
  EmojiEvents,
  Schedule,
  Timeline,
  Refresh,
  Download,
} from '@mui/icons-material';
import apiService from '../services/api';
import { Score } from '../types';

interface UserStats {
  total_matches: number;
  total_wins: number;
  total_losses: number;
  win_rate: number;
  current_streak: number;
  longest_streak: number;
  average_score: number;
  best_performance: number;
  recent_matches: Array<{
    id: number;
    opponent: string;
    result: 'win' | 'loss';
    score: string;
    date: string;
  }>;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scores-tabpanel-${index}`}
      aria-labelledby={`scores-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const ScoresPage: React.FC = () => {
  const [scores, setScores] = useState<Score[]>([]);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchScores();
    fetchUserStats();
  }, []);

  const fetchScores = async () => {
    try {
      setLoading(true);
      // First get the current user to fetch their scores
      const userResponse = await apiService.getCurrentUser();
      const user = userResponse.data;
      
      if (user && user.id) {
        // Fetch scores for the current user
        const scoresResponse = await apiService.getUserScores(user.id);
        const data: any = scoresResponse.data;
        const scores = Array.isArray(data?.scores) ? data.scores : (Array.isArray(data) ? data : []);
        setScores(scores);
      } else {
        setScores([]);
      }
    } catch (error) {
      console.error('Error fetching scores:', error);
      setScores([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await apiService.getCurrentUser();
      const user = response.data;
      
      // Create UserStats object from User data
      const userStatsData: UserStats = {
        total_matches: user.total_matches || 0,
        total_wins: Math.round((user.total_matches || 0) * (user.win_rate || 0) / 100),
        total_losses: (user.total_matches || 0) - Math.round((user.total_matches || 0) * (user.win_rate || 0) / 100),
        win_rate: user.win_rate || 0,
        current_streak: 0, // Not available in User object
        longest_streak: 0, // Not available in User object
        average_score: 0, // Not available in User object
        best_performance: 0, // Not available in User object
        recent_matches: [], // Not available in User object
      };
      
      setUserStats(userStatsData);
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  const userScores = scores.filter(score => 
    score.match_id // Simple filter for now
  );

  const renderScoreCard = (score: Score) => {
    return (
      <Card key={score.id} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
        <Box>
              <Typography variant="h6" gutterBottom>
                Match #{score.match_id}
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Chip
                  label={score.confirmed ? 'Confirmed' : 'Pending'}
                  color={score.confirmed ? 'success' : 'warning'}
              size="small"
                />
        <Chip
                  label={`Winner: Team ${score.winner_team}`}
                  color="primary"
          size="small"
        />
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {new Date(score.created_at).toLocaleDateString()}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Schedule fontSize="small" />
            <Typography variant="body2">
              {new Date(score.created_at).toLocaleDateString()}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Score Details:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sets: {score.sets.length} | Winner Team: {score.winner_team} | Status: {score.confirmed ? 'Confirmed' : 'Pending'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Scores & Statistics
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track your performance and match history
        </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              fetchScores();
              fetchUserStats();
            }}
          >
            Refresh
          </Button>
        <Button
            variant="outlined"
            startIcon={<Download />}
        >
            Export Data
        </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SportsTennis color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{userStats?.total_matches || 0}</Typography>
                  <Typography color="textSecondary">Total Matches</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUp color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{userStats?.win_rate || 0}%</Typography>
                  <Typography color="textSecondary">Win Rate</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <EmojiEvents color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{userStats?.total_wins || 0}</Typography>
                  <Typography color="textSecondary">Total Wins</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Timeline color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{userStats?.current_streak || 0}</Typography>
                  <Typography color="textSecondary">Current Streak</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Performance Overview */}
      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
        <Box flex={1}>
          <Card>
            <CardHeader title="Performance Overview" />
            <CardContent>
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Win Rate</Typography>
                  <Typography variant="body2">{userStats?.win_rate || 0}%</Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={userStats?.win_rate || 0}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Average Score</Typography>
                  <Typography variant="body2">{userStats?.average_score || 0}</Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(userStats?.average_score || 0) * 10}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              <Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Best Performance</Typography>
                  <Typography variant="body2">{userStats?.best_performance || 0}</Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(userStats?.best_performance || 0) * 10}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardHeader title="Recent Matches" />
            <CardContent>
              {userStats?.recent_matches && userStats.recent_matches.length > 0 ? (
                <List>
                  {userStats.recent_matches.slice(0, 5).map((match, index) => (
                    <React.Fragment key={match.id}>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar>
                            <SportsTennis />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`vs ${match.opponent}`}
                          secondary={`${match.score} • ${match.date}`}
                        />
                        <Chip
                          label={match.result}
                          color={match.result === 'win' ? 'success' : 'error'}
                          size="small"
                        />
                      </ListItem>
                      {index < userStats.recent_matches.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box textAlign="center" py={2}>
                  <SportsTennis sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No recent matches
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab 
            label={
              <Badge badgeContent={userScores.length} color="primary">
                My Scores
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={scores.length} color="secondary">
                All Scores
              </Badge>
            } 
          />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {userScores.length === 0 ? (
          <Box textAlign="center" py={4}>
            <ScoreIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No scores yet
            </Typography>
            <Typography color="text.secondary" paragraph>
              Play some matches to see your scores here!
            </Typography>
          </Box>
        ) : (
          userScores.map(renderScoreCard)
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {scores.length === 0 ? (
          <Box textAlign="center" py={4}>
            <ScoreIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No scores available
            </Typography>
            <Typography color="text.secondary" paragraph>
              Check back later for match scores!
            </Typography>
          </Box>
        ) : (
          scores.map(renderScoreCard)
        )}
      </TabPanel>
    </Box>
  );
}; 
